import { SkeletonCircle } from '@/components/ui/skeleton'
import { userId } from '@stateApp'
import { useQuery } from '@tanstack/solid-query'
import { Show } from 'solid-js'
import { Circle } from 'styled-system/jsx'
import { avatarQueryOpts } from './fetchImageUrl'

// The prototype generates a gradient + white silhouette avatar per user
// (a seeded hash picking one of 6 hues). Rather than reimplementing that
// generator, this is one fixed instance of its exact output — a green/teal
// gradient closest to the app's own jade accent — used as the fallback for
// anyone without an uploaded profile picture.
const DEFAULT_AVATAR = `data:image/svg+xml,${encodeURIComponent(
  "<svg xmlns='http://www.w3.org/2000/svg' width='80' height='80'>" +
  "<defs><linearGradient id='g' x1='0' y1='0' x2='1' y2='1'>" +
  "<stop offset='0' stop-color='hsl(150 55% 60%)'/>" +
  "<stop offset='1' stop-color='hsl(190 55% 44%)'/>" +
  "</linearGradient></defs>" +
  "<rect width='80' height='80' fill='url(#g)'/>" +
  "<circle cx='40' cy='31' r='14' fill='rgba(255,255,255,.92)'/>" +
  "<path d='M13 74c0-16 12-25 27-25s27 9 27 25z' fill='rgba(255,255,255,.92)'/>" +
  '</svg>'
)}`

type AvatarProps = {
  id?: string
}

export function Avatar(props: AvatarProps) {
  // If no ID is passed in props, automatically fallback to the logged-in user's ID
  const targetId = () => props.id || userId()

  const q = useQuery(() => avatarQueryOpts(targetId()))

  return (
    <Show
      when={!q.isLoading}
      fallback={<SkeletonCircle h="full" aspectRatio="square" />}
    >
      <Circle
        h="full"
        aspectRatio="square"
        border="solid 1px"
        borderColor="fg.subtle"
        backgroundSize="cover"
        backgroundPosition="center center"
        // Double-quoted: the default SVG data URI contains literal
        // parentheses (rgba(), hsl(), url(#g)) and single quotes (the
        // SVG's own attribute syntax) that encodeURIComponent doesn't
        // escape — an unquoted url(...) would terminate at the first of
        // those and silently render nothing. Double quotes are safe since
        // nothing in the SVG uses them.
        style={{ 'background-image': `url("${q.data || DEFAULT_AVATAR}")` }}
      />
    </Show>
  )
}
